import { useEffect, useRef } from 'react';
import { BarChart, GaugeChart, HeatmapChart, LineChart, PieChart, RadarChart, ScatterChart } from 'echarts/charts';
import {
  CalendarComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  SingleAxisComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  TransformComponent,
  VisualMapComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import type { CSSProperties } from 'react';

import type {
  BarChartOption,
  GaugeChartOption,
  HeatmapChartOption,
  LineChartOption,
  PieChartOption,
  RadarChartOption,
  ScatterChartOption,
  WordChartOption,
} from '../../models/echarts';

echarts.use([
  BarChart,
  GaugeChart,
  HeatmapChart,
  LineChart,
  PieChart,
  RadarChart,
  ScatterChart,
  CalendarComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  SingleAxisComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  TransformComponent,
  VisualMapComponent,
  CanvasRenderer,
  LabelLayout,
  UniversalTransition,
]);

export const ReactECharts = ({
  option,
  style,
}: {
  option:
    | BarChartOption
    | GaugeChartOption
    | HeatmapChartOption
    | LineChartOption
    | PieChartOption
    | RadarChartOption
    | ScatterChartOption
    | WordChartOption;
  style?: CSSProperties;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const height = style?.minHeight ? undefined : style?.height || 600;

  useEffect(() => {
    let chart: echarts.ECharts | undefined = undefined;
    const resizeHandler = () => {
      chart?.resize();
    };

    if (ref.current) {
      chart = echarts.init(ref.current, null, { renderer: 'canvas', height: 'auto', width: 'auto' });
      chart.setOption(option);
      window.addEventListener('resize', resizeHandler);
    }

    return () => {
      chart?.dispose();
      window.removeEventListener('resize', resizeHandler);
    };
  }, [option]);

  return <div ref={ref} style={{ ...style, height, width: style?.width || 'auto' }} />;
};
